import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Spinner from '../../components/Spinner'
import TermPicker from '../../components/TermPicker'
import { getProduct } from '../../api/products'
import { calcPreviewByPrice, createOrder } from '../../api/orders'
import { getSettings } from '../../api/settings'
import { useAuth } from '../../context/AuthContext'
import { useTelegram } from '../../hooks/useTelegram'
import { CreditSettings } from '../../types'

const API = import.meta.env.VITE_API_URL ?? ''

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

function getRate(s: CreditSettings, months: number): number {
  if (months <= 3) return s.rate_3_months
  if (months <= 6) return s.rate_6_months
  if (months <= 9) return s.rate_9_months
  return s.rate_12_months
}

const fmt  = (n: number) => `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`
const fmtS = (n: number, rate: number) => `≈ ${Math.round(n * rate).toLocaleString('ru-RU')} сум`

export default function ProductPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tg } = useTelegram()
  const lang = user?.language ?? 'ru'

  const [downAmt, setDownAmt] = useState(0)
  const [downInput, setDownInput] = useState('0')
  const [months, setMonths] = useState(12)
  const [activeImg, setActiveImg] = useState(0)

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProduct(Number(id)),
    enabled: !!id,
  })
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings })

  const maxMonths    = settings?.max_months ?? 24
  const usdRate      = settings?.usd_rate ?? 12000
  const interestRate = settings ? getRate(settings, months) : null

  const { data: preview, isFetching: calcLoading } = useQuery({
    queryKey: ['calc', id, downAmt, months, interestRate],
    queryFn: () => calcPreviewByPrice({
      product_price: product!.price,
      down_payment: downAmt,
      months,
      discount_pct: user?.personal_discount ?? 0,
    }),
    enabled: !!id && !!product,
    placeholderData: (prev) => prev,
  })

  const createMut = useMutation({
    mutationFn: () => createOrder({ product_id: Number(id), down_payment: downAmt, months }),
    onSuccess: (order) => {
      tg?.HapticFeedback.notificationOccurred('success')
      toast.success(lang === 'uz' ? 'Ariza yaratildi!' : 'Заявка создана!')
      navigate(`/orders/${order.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? (lang === 'uz' ? 'Xatolik' : 'Ошибка')),
  })

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  if (isLoading) return <Spinner className="h-screen" />
  if (!product) return null

  const name    = lang === 'uz' ? product.name_uz : product.name_ru
  const desc    = lang === 'uz' ? product.description_uz : product.description_ru
  const catName = product.category ? (lang === 'uz' ? product.category.name_uz : product.category.name_ru) : null

  const allImages = [
    ...(product.image_path ? [product.image_path] : []),
    ...(product.extra_images ?? []),
  ]
  const currentImg = allImages[activeImg] ?? null

  const AMT_OPTIONS  = [0, 50, 100, 200].filter(a => a <= product.price)

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {catName ?? (lang === 'uz' ? 'Katalog' : 'Каталог')}
        </span>
      </header>

      <main style={{ flex: 1, padding: '0 0 40px', overflowY: 'auto' }}>
        {/* Product image hero */}
        <div style={{ height: 220, position: 'relative', overflow: 'hidden', background: 'rgba(255,248,235,0.03)' }}>
          {currentImg
            ? <img src={`${API}/uploads/${currentImg}`} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <span style={{ fontSize: 72 }}>📦</span>
              </div>
          }
          {allImages.length > 1 && (
            <div style={{ position: 'absolute', bottom: 10, right: 12, background: 'rgba(0,0,0,0.55)', borderRadius: 99, padding: '2px 9px', fontSize: 10, color: TX }}>
              {activeImg + 1}/{allImages.length}
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {allImages.length > 1 && (
          <div style={{ display: 'flex', gap: 6, padding: '10px 20px', overflowX: 'auto', borderBottom: `1px solid ${BD}` }}>
            {allImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                style={{
                  width: 50, height: 50, flexShrink: 0, borderRadius: 8, overflow: 'hidden',
                  border: i === activeImg ? `2px solid ${G}` : '2px solid transparent',
                  padding: 0, cursor: 'pointer',
                }}
              >
                <img src={`${API}/uploads/${img}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        )}

        <div style={{ padding: '20px 20px 0' }}>
          {/* Product info */}
          {catName && (
            <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {catName}
            </p>
          )}
          <h1 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 26, color: TX, margin: '0 0 16px', lineHeight: 1.15 }}>
            {name}
          </h1>

          {/* Price */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}`, marginBottom: 24 }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em' }}>
                {lang === 'uz' ? 'NARX' : 'ЦЕНА'}
              </p>
              <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 22, color: TX }}>
                {fmt(product.price)}
              </p>
              <p style={{ margin: '2px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3 }}>
                {fmtS(product.price, usdRate)}
              </p>
            </div>
          </div>

          {/* Term selection */}
          <p style={{ fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 12 }}>
            {lang === 'uz' ? "BO'LIB TO'LASH MUDDATI" : 'СРОК РАССРОЧКИ'}
          </p>
          <div style={{ marginBottom: 24 }}>
            <TermPicker value={months} onChange={setMonths} max={maxMonths} lang={lang} />
          </div>

          {/* Down payment */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 12 }}>
              {lang === 'uz' ? "BOSHLANG'ICH TO'LOV" : 'ПЕРВОНАЧАЛЬНЫЙ ВЗНОС'}
            </p>

            {/* Input */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px', borderRadius: 12, marginBottom: 10,
              background: S, border: `1px solid ${BD}`,
            }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, color: G }}>$</span>
              <input
                type="number" inputMode="decimal" min={0} max={product.price} step={1}
                value={downInput}
                onChange={e => {
                  const v = e.target.value
                  setDownInput(v)
                  const n = Math.min(parseFloat(v) || 0, product.price)
                  setDownAmt(Math.round(n * 100) / 100)
                }}
                style={{ background: 'none', border: 'none', outline: 'none', flex: 1, fontFamily: "'JetBrains Mono', monospace", fontSize: 20, color: TX }}
              />
            </div>
            {downAmt > 0 && (
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3, marginBottom: 10 }}>
                {fmtS(downAmt, usdRate)}
              </p>
            )}

            {/* Slider */}
            <input
              type="range" min={0} max={product.price} step={10} value={downAmt}
              onChange={e => { const v = Number(e.target.value); setDownAmt(v); setDownInput(String(v)) }}
              style={{ width: '100%' }}
            />

            {/* Quick preset buttons */}
            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              {AMT_OPTIONS.map(a => (
                <button
                  key={a}
                  onClick={() => { setDownAmt(a); setDownInput(String(a)) }}
                  style={{
                    padding: '5px 12px', borderRadius: 99,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    background: downAmt === a ? GB : S,
                    color: downAmt === a ? G : TX3,
                    border: downAmt === a ? `1px solid ${G}` : `1px solid ${BD}`,
                    cursor: 'pointer',
                  }}
                >
                  ${a}
                </button>
              ))}
            </div>
          </div>

          {/* Calculation result */}
          {preview && !calcLoading && (
            <>
              {preview.dp_warning && (
                <div style={{
                  padding: '10px 14px', borderRadius: 10, marginBottom: 12,
                  background: 'rgba(234,179,8,0.09)', border: '1px solid rgba(234,179,8,0.30)',
                }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#EAB308' }}>
                    ⚠️ {lang === 'uz'
                      ? `Boshlang'ich to'lov tovar narxining 15% dan kam — qo'shimcha +${preview.dp_surcharge}% stavka qo'llanadi`
                      : `Первоначальный взнос менее 15% от цены — применяется надбавка +${preview.dp_surcharge}% к ставке`}
                  </p>
                </div>
              )}
              <div style={{ padding: 20, borderRadius: 16, ...GLASS, border: `1px solid ${BD}`, marginBottom: 20 }}>
                <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 600, color: G, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                  {lang === 'uz' ? 'OYLIK TO\'LOV' : 'ЕЖЕМЕСЯЧНЫЙ ПЛАТЁЖ'}
                </p>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 36, color: TX }}>{fmt(preview.monthly_payment)}</span>
                </div>
                <p style={{ margin: '0 0 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3 }}>
                  {fmtS(preview.monthly_payment, usdRate)}
                </p>
                <p style={{ margin: '0 0 16px', fontSize: 12, color: TX2 }}>
                  {months} {lang === 'uz' ? "to'lov" : 'платежей'} · {lang === 'uz' ? 'Stavka' : 'Ставка'} {preview.interest_rate}%
                </p>
                <div style={{ borderTop: `1px solid ${BD}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <CalcRow label={lang === 'uz' ? "Raqamlashtirish" : 'Сумма рассрочки'} value={fmt(preview.loan_amount)} hint={fmtS(preview.loan_amount, usdRate)} />
                  <CalcRow label={lang === 'uz' ? 'Jami' : 'Итого'} value={fmt(preview.total_amount)} hint={fmtS(preview.total_amount, usdRate)} />
                </div>
              </div>
            </>
          )}
          {calcLoading && <Spinner className="my-4" />}

          {desc && (
            <p style={{ fontSize: 13, color: TX2, lineHeight: 1.6, marginBottom: 20 }}>{desc}</p>
          )}

          {/* CTA */}
          <button
            onClick={() => createMut.mutate()}
            disabled={months < 1 || createMut.isPending}
            style={{
              width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 14, fontWeight: 600,
              background: GB, border: `1.5px solid ${G}`, color: G,
              cursor: months >= 1 ? 'pointer' : 'not-allowed',
              opacity: createMut.isPending ? 0.6 : 1,
              marginBottom: 32,
            }}
          >
            {createMut.isPending
              ? (lang === 'uz' ? 'Yuklanmoqda...' : 'Загрузка...')
              : (lang === 'uz' ? "Bo'lib to'lashga rasmiylashtirish" : 'Оформить рассрочку')}
          </button>
        </div>
      </main>
    </div>
  )
}

function CalcRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <span style={{ fontSize: 12, color: '#8A8480' }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#F4F1EA' }}>{value}</span>
        {hint && <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#8A8480', margin: '1px 0 0' }}>{hint}</p>}
      </div>
    </div>
  )
}
